import React, { useState, useEffect, useRef } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { MdRemoveRedEye } from "react-icons/md";
import { BiTransfer } from "react-icons/bi";
import { BsFillPersonPlusFill } from "react-icons/bs";
import {
  pendingTransactions,
  recipientList,
  transactionHistory,
  userProfile,
  fetchAccountUsage,
} from "../../utils/Api";
import { commaSeperator } from "../../utils/hook";
import { Alert, Modal } from "react-bootstrap";
import important from "../../assets/img/userdashboard/important.png";
import ParentComp from "../kyc/ParentComp";
import { accessProvider } from "../../hooks/validator";

const Dashboard = () => {
  const navigate = useNavigate();
  const [profileLoaded, setProfileLoaded] = useState(false);

  const [transactionData, setTransactionData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recipientData, setRecipientData] = useState([]);
  const [total_recipients, setTotalRecipients] = useState(0);
  const [verificationModal, setVerificationModal] = useState({
    toggle: false,
    step: 1,
  });
  const [verification_status, setVerificationStatus] = useState("pending");
  const [verification_reason, setVerificationReason] = useState(null);
  const [user, setUser] = useState(
    JSON.parse(sessionStorage.getItem("remi-user-dt")) || {},
  );
  const [openDropdown, setOpenDropdown] = useState(null);
  const [tier_data, setTierData] = useState(null);

  // True when the admin has already approved this user's digital ID
  // (admin-onboarded) but their profile/KYC details are still incomplete.
  // These users can already transfer money, so we must NOT show the
  // "To transfer Money, Please complete the below steps" messaging for
  // them — just a gentle prompt to finish their profile/KYC.
  const isAdminOnboardedProfileIncomplete =
    user?.is_digital_Id_verified?.toLowerCase() === "approved" &&
    !user.profile_completed;

  const dropdownRef = useRef(null);

  const toggleDropdown = (id) => {
    setOpenDropdown((prev) => (prev === id ? null : id));
  };

  const transHistory = async () => {
    let list = [];

    const res = await transactionHistory();
    if (res.code === "200") {
      const { data } = res.data;
      list = [...data];
    }

    const incomplete = await pendingTransactions();
    if (incomplete.code === "200" && Array.isArray(incomplete.data)) {
      list = [...list, ...incomplete.data];
      list.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
    }

    setTransactionData(list.length > 5 ? list.slice(0, 5) : list);
    setLoading(false);
  };

  useEffect(() => {
    if (verification_status === "approved") {
      fetchAccountUsage().then((res) => {
        if (res?.code === "200") {
          setTierData({
            count:
              res?.data?.transactions_left !== undefined &&
              res?.data?.transactions_left !== null
                ? res.data.transactions_left
                : "N/A",
            limit:
              res?.data?.amount_left !== undefined &&
              res?.data?.amount_left !== null
                ? res.data.amount_left
                : "N/A",
            is_unlimited: res?.data?.unlimited ?? false,
          });
        }
      });
    }
  }, [verification_status]);

  const getList = () => {
    recipientList({})
      .then(function (response) {
        if (response.code == "200") {
          let list = [];
          if (response.data.length > 5) {
            for (let i = 0; i < 5; i++) {
              list.push(response.data[i]);
            }
          } else {
            list = response.data;
          }
          setRecipientData(list);
          setTotalRecipients(response.data.length);
          setLoading(false);
        }
      })
      .catch(function (error) {
        setLoading(false);
      });
  };

  const fetchData = async () => {
    try {
      const response = await userProfile();
      if (response && response.code === "200") {
        setUser(response.data);

        const result = accessProvider(
          response.data.is_digital_Id_verified.toLowerCase(),
          response.data.veriff_status.toLowerCase(),
        );
        setVerificationStatus(result.status);
        setVerificationReason(result.reason);

        sessionStorage.setItem("remi-user-dt", JSON.stringify(response.data));
      }
    } finally {
      setProfileLoaded(true);
    }
  };

  useEffect(() => {
    fetchData();
    transHistory();
    getList();
  }, []);

  const modified_date = (date) => {
    let d = date.split("-");
    return d[2] + "-" + d[1] + "-" + d[0];
  };

  const start = (val) => {
    userProfile().then((res) => {
      if (res.code === "200") {
        const result = accessProvider(
          res.data.is_digital_Id_verified.toLowerCase(),
          res.data.veriff_status.toLowerCase(),
        );
        if (
          (result.status?.toLowerCase() !== "approved" &&
            result.status?.toLowerCase() !== "submitted") ||
          res?.data.profile_completed == false
        ) {
          setVerificationModal({ toggle: true, step: val });
        } else {
          window.location.reload();
        }
      }
    });
  };

  const end = () => {
    setVerificationModal({ toggle: false, step: 1 });
    fetchData();
  };

  const downloadReceipt = (id) => {
    window.open(`${process.env.REACT_APP_API_URL}/payment/receipt/${id}`);
  };

  return (
    <section className="dashboard">
      <div className="row">
        <div className="col-md-12">
          <div className="form-head mb-4">
            <div className="d-flex justify-content-between align-items-center flex-wrap">
              <h2 className="text-black font-w600 mb-0">
                <b>
                  Welcome,{" "}
                  <span style={{ color: "#6414e9" }}>{user.First_name}</span>
                </b>
              </h2>

              {verification_status === "approved" && tier_data !== null && (
                <div className="tier-info-box">
                  {tier_data.is_unlimited ? (
                    <div className="unlimited-badge">
                      <span className="badge bg-success fs-14 px-3 py-2">
                        Unlimited Transactions
                      </span>
                    </div>
                  ) : (
                    <div className="limit-info d-flex gap-3">
                      <div className="limit-item">
                        <span className="limit-label text-muted">
                          Transactions Left:
                        </span>
                        <span className="limit-value fw-bold ms-2">
                          {tier_data.count === "N/A" ? "N/A" : tier_data.count}
                        </span>
                      </div>
                      <div className="limit-item">
                        <span className="limit-label text-muted">
                          Amount Left:
                        </span>
                        <span className="limit-value fw-bold ms-2">
                          {tier_data.limit === "N/A"
                            ? "N/A"
                            : `$${commaSeperator(tier_data.limit)}`}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Tiers Alert */}
          {user.TiersAlert && !loading && (
            <Alert
              className="verify-alert mb-3"
              style={{
                backgroundColor: "#fef0ef",
                borderColor: "#ff000033",
                border: "1px solid #ff000029",
                borderRadius: "10px",
              }}
            >
              <b style={{ color: "#4a4a4a" }}>
                {user.TiersAlert.split("RemitAssure Support")[0]}
                <Link
                  to="/help"
                  className="fw-bold text-decoration-underline"
                  style={{ color: "#3cc4f5" }}
                >
                  RemitAssure Support
                </Link>
                {user.TiersAlert.split("RemitAssure Support")[1]}
              </b>
            </Alert>
          )}

          {/* KYC Verification Alerts — each condition gets its own separate box */}
          {profileLoaded && !loading && (
            <>
              {/*
              {isAdminOnboardedProfileIncomplete && (
                <Alert
                  className="verify-alert mb-3"
                  style={{
                    backgroundColor: "#fef0ef",
                    borderColor: "#ff000033",
                    border: "1px solid #ff000029",
                    borderRadius: "10px",
                  }}
                >
                  <span className="fw-bold" style={{ color: "#4a4a4a" }}>
                    Please complete your profile.
                  </span>
                  <br />
                  <button
                    className="btn-rounded mt-2"
                    style={{
                      border: "1px solid #ff000033",
                      color: "#d94040",
                      backgroundColor: "transparent",
                      padding: "5px 30px",
                      borderRadius: "30px",
                      fontWeight: "bold",
                    }}
                    onClick={() => start(1)}
                  >
                    Update
                  </button>
                </Alert>
              )}
              */}

              {verification_status === "submitted" && (
                <Alert
                  className="verify-alert mb-3"
                  style={{
                    backgroundColor: "#fef0ef",
                    borderColor: "#ff000033",
                    border: "1px solid #ff000029",
                    borderRadius: "10px",
                  }}
                >
                  <span className="fw-bold" style={{ color: "#4a4a4a" }}>
                    Your KYC has been submitted, please wait for approval.
                  </span>
                </Alert>
              )}

              {verification_status === "declined" && (
                <Alert
                  className="verify-alert mb-3"
                  style={{
                    backgroundColor: "#fef0ef",
                    borderColor: "#ff000033",
                    border: "1px solid #ff000029",
                    borderRadius: "10px",
                  }}
                >
                  {verification_reason === "access_denied" ? (
                    <span className="fw-bold" style={{ color: "#4a4a4a" }}>
                      There has been a problem with your ID verification.
                      Please contact{" "}
                      <Link
                        to="/help"
                        className="fw-bold text-decoration-underline "
                        style={{ color: "#3cc4f5" }}
                      >
                        RemitAssure Support
                      </Link>
                      .
                    </span>
                  ) : verification_reason === "veriff_failed" ? (
                    <span className="fw-bold" style={{ color: "#4a4a4a" }}>
                      Your KYC has been submitted, please wait for approval.
                    </span>
                  ) : (
                    <>
                      <span className="fw-bold" style={{ color: "#4a4a4a" }}>
                        Your ID verification couldn't be completed. Please
                        resubmit your details.
                      </span>
                      <br />
                      <button
                        className=" btn-rounded mt-2"
                        style={{
                          border: "1px solid #ff000033",
                          color: "#d94040",
                          backgroundColor: "transparent",
                          padding: "5px 30px",
                          borderRadius: "30px",
                          fontWeight: "bold",
                        }}
                        onClick={() => start(3)}
                      >
                        Re-verify
                      </button>
                    </>
                  )}
                </Alert>
              )}

              {verification_status === "suspended" && (
                <Alert
                  className="verify-alert mb-3"
                  style={{
                    backgroundColor: "#fef0ef",
                    borderColor: "#ff000033",
                    border: "1px solid #ff000029",
                    borderRadius: "10px",
                  }}
                >
                  <b style={{ color: "#d94040" }}>
                    To transfer Money, Please complete the below steps:
                  </b>
                  <br />
                  <span className="fw-bold" style={{ color: "#4a4a4a" }}>
                    Your KYC has been {verification_status}, please contact
                    admin for information.
                  </span>
                </Alert>
              )}

              {!isAdminOnboardedProfileIncomplete && !user.profile_completed && (
                <Alert
                  className="verify-alert mb-3"
                  style={{
                    backgroundColor: "#fef0ef",
                    borderColor: "#ff000033",
                    border: "1px solid #ff000029",
                    borderRadius: "10px",
                  }}
                >
                  <b style={{ color: "#d94040" }}>
                    To transfer Money, Please complete the below steps:
                  </b>
                  <br />
                  <span className="fw-bold" style={{ color: "#4a4a4a" }}>
                    Complete Your KYC.
                  </span>
                  <br />
                  <button
                    className="btn-rounded mt-2"
                    style={{
                      border: "1px solid #ff000033",
                      color: "#d94040",
                      backgroundColor: "transparent",
                      padding: "5px 30px",
                      borderRadius: "30px",
                      fontWeight: "bold",
                    }}
                    onClick={() => start(1)}
                  >
                    Update
                  </button>
                </Alert>
              )}

              {user.profile_completed && verification_status === "pending" && (
                <Alert
                  className="verify-alert mb-3"
                  style={{
                    backgroundColor: "#fef0ef",
                    borderColor: "#ff000033",
                    border: "1px solid #ff000029",
                    borderRadius: "10px",
                  }}
                >
                  <b style={{ color: "#d94040" }}>
                    To transfer Money, Please complete the below steps:
                  </b>
                  <br />
                  <span className="fw-bold" style={{ color: "#4a4a4a" }}>
                    Verify Your Account.
                  </span>
                  <br />
                  <button
                    className=" btn-rounded mt-2"
                    style={{
                      border: "1px solid #ff000033",
                      color: "#d94040",
                      backgroundColor: "transparent",
                      padding: "5px 30px",
                      borderRadius: "30px",
                      fontWeight: "bold",
                    }}
                    onClick={() => start(3)}
                  >
                    Verify
                  </button>
                </Alert>
              )}
            </>
          )}

          <div className="row g-3">
            <div className="col-xl-4 col-lg-4 col-md-6 fullwidth">
              <div className="dashbord-user dCard-1">
                <div className="dashboard-content">
                  <div className="d-flex justify-content-between">
                    <div className="">
                      <div className="top-content">
                        <h3>Recipeints</h3>
                      </div>
                      <div className="user-count">
                        <span className="text-uppercase-edit">
                          View the list of Recipients
                        </span>
                      </div>
                    </div>
                    <div className="icon">
                      <BsFillPersonPlusFill />
                    </div>
                  </div>
                  <div className="mt-3">
                    <NavLink
                      to="/user-recipients"
                      className="btn btn-outline-dark btn-rounded"
                    >
                      View
                    </NavLink>
                    <span className="text-light custom-number">
                      Recipients ⇒ {total_recipients}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-xl-4 col-lg-4 col-md-6 fullwidth">
              <div className="dashbord-user dCard-1 middle-card">
                <div className="dashboard-content">
                  <div className="d-flex justify-content-between">
                    <div className="">
                      <div className="top-content">
                        <h3>New Transfer</h3>
                      </div>
                      <div className="user-count">
                        <span className="text-uppercase-edit">
                          Easy way to send money
                        </span>
                      </div>
                    </div>
                    <div className="icon">
                      <BiTransfer />
                    </div>
                  </div>
                  <div className="mt-3">
                    <NavLink
                      to={`/user-send-money`}
                      className="btn btn-outline-dark btn-rounded"
                    >
                      Send Money
                    </NavLink>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-xl-4 col-lg-4 col-md-6 fullwidth">
              <div className="dashbord-user dCard-1">
                <div className="dashboard-content">
                  <div className="d-flex justify-content-between">
                    <div className="">
                      <div className="top-content">
                        <h3>Profile</h3>
                      </div>
                      <div className="user-count">
                        <span className="text-uppercase-edit">
                          Check your profile
                        </span>
                      </div>
                    </div>
                    <div className="icon">
                      <MdRemoveRedEye />
                    </div>
                  </div>
                  <div className="mt-3">
                    <NavLink
                      to="/user-profile"
                      className="btn btn-outline-dark btn-rounded"
                    >
                      View
                    </NavLink>
                    <span className="text-light custom-number">
                      {user.First_name} {user.Last_name}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {loading && (
        <div className="loader-overly">
          <div className="loader"></div>
        </div>
      )}

      {!loading && (
        <>
          <div className="row">
            <div className="col-xl-8">
              <div className="card">
                <div className="card-header d-block d-sm-flex border-0">
                  <div className="me-3">
                    <h4 className="fs-20 text-black">Latest Transactions</h4>
                  </div>
                </div>
                {transactionData?.length != 0 ? (
                  <div className="table-responsive">
                    <table className="table table-responsive-md card-table previous-transactions">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Recipient</th>
                          <th>Amount Paid</th>
                          <th>Status</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {transactionData?.map((res, index) => {
                          return (
                            <tr style={{ cursor: "pointer" }} key={index}>
                              <td
                                onClick={() =>
                                  navigate(
                                    `/transaction-detail/${res?.transaction_id}`,
                                  )
                                }
                              >
                                <h6 className="fs-16 text-black font-w400 mb-0">
                                  {modified_date(res?.date)}
                                </h6>
                              </td>
                              <td
                                onClick={() =>
                                  navigate(
                                    `/transaction-detail/${res?.transaction_id}`,
                                  )
                                }
                              >
                                <h6 className="fs-16 font-w600 mb-0">
                                  <span className="text-black">
                                    {res?.recipient_business_name?.trim()
                                      ? res.recipient_business_name
                                      : res?.recipient_name || "N/A"}
                                  </span>
                                </h6>
                              </td>
                              <td
                                onClick={() =>
                                  navigate(
                                    `/transaction-detail/${res?.transaction_id}`,
                                  )
                                }
                              >
                                <span className="fs-16 text-black font-w500">
                                  <span className="text-capitalize">
                                    {res?.send_currency}{" "}
                                  </span>{" "}
                                  {commaSeperator(res?.amount)}
                                </span>
                              </td>
                              <td>
                                {res?.payment_status === "cancelled" ||
                                res?.payment_status === "Cancelled" ? (
                                  <span className="text-danger fs-16 font-w500 d-block">
                                    <span
                                      className="btn btn-outline-danger btn-rounded custom_status"
                                      onClick={() =>
                                        navigate(
                                          `/transaction-detail/${res?.transaction_id}`,
                                        )
                                      }
                                    >
                                      {res?.payment_status}
                                    </span>
                                  </span>
                                ) : (
                                  <span className="text-success fs-16 font-w500 d-block">
                                    <span
                                      className="btn btn-outline-success btn-rounded custom_status"
                                      onClick={() =>
                                        navigate(
                                          `/transaction-detail/${res?.transaction_id}`,
                                        )
                                      }
                                    >
                                      {res?.payment_status}
                                    </span>
                                  </span>
                                )}
                              </td>
                              <td className="position-relative">
                                {res?.payment_status?.toLowerCase() ===
                                  "incomplete" && (
                                  <button
                                    className="btn btn-outline-success btn-rounded btn-fixed"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate("/user-send-money", {
                                        state: { transaction_id: res?.id },
                                      });
                                    }}
                                  >
                                    <span>Continue</span>
                                  </button>
                                )}

                                {res?.payment_status?.toLowerCase() !==
                                  "cancelled" &&
                                  res?.payment_status?.toLowerCase() !==
                                    "expired" &&
                                  res?.payment_status?.toLowerCase() !==
                                    "incomplete" && (
                                    <button
                                      className="btn btn-outline-success btn-rounded btn-fixed"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        downloadReceipt(res?.id);
                                      }}
                                    >
                                      Download
                                    </button>
                                  )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-responsive-md card-table previous-transactions">
                      <thead></thead>
                      <tbody></tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
            <div className="col-xl-4">
              <div className="card">
                <div className="card-header d-block d-sm-flex border-0">
                  <div className="me-3">
                    <h4 className="fs-20 text-black">Recent Recipients</h4>
                  </div>
                </div>
                {recipientData?.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table table-responsive-md card-table previous-transactions">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Destination</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recipientData?.map((res, index) => {
                          return (
                            <tr key={res.id}>
                              <td>
                                <div className="me-auto">
                                  <h6 className="fs-16 font-w600 mb-0">
                                    {res?.account_type === "business"
                                      ? res?.business_name
                                      : `${res?.first_name || ""} ${
                                          res?.last_name || ""
                                        }`}
                                  </h6>
                                  <span className="fs-12">{res?.date}</span>
                                </div>
                              </td>
                              <td>{res?.country}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-responsive-md card-table previous-transactions">
                      <thead></thead>
                      <tbody></tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      <Modal
        show={verificationModal.toggle}
        size="xl"
        backdrop="static"
        onHide={() => end()}
        centered
      >
        <Modal.Header closeButton>
          <b style={{ color: "#190079" }}>Complete Your KYC</b>
        </Modal.Header>
        <Modal.Body className="w-100 m-auto">
          <ParentComp
            handleModel={() => end()}
            is_model={verificationModal.toggle}
            currentStep={verificationModal.step}
          />
        </Modal.Body>
      </Modal>
    </section>
  );
};

export default Dashboard;